import * as React from 'react'
import { Card } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { PageImageViewer, PageStatusChip, percent } from '../../IngestPage/shared/PageImageViewer'
import type { ConfigSubject } from '../util/subject'

export function PagesTab({ subject }: { subject: ConfigSubject }) {
  const [selectedId, setSelectedId] = React.useState(subject.pagePreviews[0]?.pageId)
  const page =
    subject.pagePreviews.find((candidate) => candidate.pageId === selectedId) ??
    subject.pagePreviews[0]
  React.useEffect(() => {
    if (!subject.pagePreviews.some((candidate) => candidate.pageId === selectedId)) {
      setSelectedId(subject.pagePreviews[0]?.pageId)
    }
  }, [selectedId, subject.pagePreviews])

  if (!page) {
    return (
      <Card className="p-3 text-xs text-ink-3">
        Page-level trial evidence is available on newly verified agent drafts.
      </Card>
    )
  }
  const hintFields = Object.entries(page.hints?.fields ?? {})
  return (
    <div className="grid min-w-0 gap-3 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <Card className="h-fit gap-1 p-2" aria-label="Verified example pages">
        {subject.pagePreviews.map((candidate) => (
          <button
            key={candidate.pageId}
            type="button"
            className={cn(
              'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-11',
              candidate.pageId === page.pageId ? 'bg-surface-2 text-ink' : 'hover:bg-surface-1'
            )}
            onClick={() => setSelectedId(candidate.pageId)}
          >
            <span className="min-w-0 truncate">
              {candidate.exampleName} · p{candidate.pageNumber}
            </span>
            <PageStatusChip status={candidate.status} />
          </button>
        ))}
      </Card>
      <Card className="min-w-0 gap-3 p-3" data-testid={`page-review-${page.pageId}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium">{page.exampleName}</span>
          <span className="text-ink-3">page {page.pageNumber}</span>
          <PageStatusChip status={page.status} />
          {page.confidence !== undefined ? (
            <span className="text-ink-3">{percent(page.confidence)} page confidence</span>
          ) : null}
        </div>
        <div className="grid min-w-0 gap-3 xl:grid-cols-2">
          <PageImageViewer
            key={page.pageId}
            imageUrl={page.imageUrl}
            preprocessedImageUrl={page.preprocessedImageUrl}
            fileName={page.exampleName}
            pageNumber={page.pageNumber}
          />
          <div className="min-w-0 space-y-3">
            {page.reason ? <p className="text-xs text-st-warn">{page.reason}</p> : null}
            {hintFields.length || page.hints?.notes ? (
              <div className="min-w-0 space-y-1 text-11" data-testid="page-hints">
                <p className="font-medium">Preprocess hints</p>
                {hintFields.length ? (
                  <dl className="grid grid-cols-[minmax(7rem,0.45fr)_minmax(0,1fr)] gap-x-2 gap-y-1">
                    {hintFields.map(([column, value]) => (
                      <React.Fragment key={column}>
                        <dt className="truncate font-mono text-ink-3">{column}</dt>
                        <dd className="break-words">{value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                ) : null}
                {page.hints?.notes ? <p className="text-ink-2">{page.hints.notes}</p> : null}
              </div>
            ) : null}
            {page.tables.map((table) => (
              <div key={table.table} className="min-w-0 space-y-1.5">
                <p className="font-mono text-11 font-medium">{table.table}</p>
                {table.rows.length === 0 ? (
                  <p className="text-11 text-ink-3">No rows attributed to this page.</p>
                ) : (
                  table.rows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      className={cn(
                        'rounded border p-2',
                        row.reviewStatus === 'needs_review'
                          ? 'border-st-warn/50 bg-st-warn-bg'
                          : 'border-line'
                      )}
                      data-testid={`page-row-${table.table}-${rowIndex}`}
                    >
                      <div className="mb-1 text-10 uppercase text-ink-3">
                        Row {rowIndex + 1} · {row.reviewStatus.replace('_', ' ')}
                      </div>
                      {row.reviewNote ? (
                        <p className="mb-1 text-11 text-st-warn">{row.reviewNote}</p>
                      ) : null}
                      <dl className="grid grid-cols-[minmax(7rem,0.45fr)_minmax(0,1fr)_auto] gap-x-2 gap-y-1 text-11">
                        {Object.entries(row.values).map(([field, value]) => (
                          <React.Fragment key={field}>
                            <dt className="truncate font-mono text-ink-3">{field}</dt>
                            <dd className="break-words">{value === null ? '—' : String(value)}</dd>
                            <dd className="tabular-nums text-ink-3">
                              {percent(row.fieldConfidence[field] ?? 0)}
                            </dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  )
}
